/**
 * Hook for monitoring long loading operations
 * Shows warnings and errors with configurable thresholds
 * Integrates with Sentry, Amplitude, and app_logs
 */

import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { slowLoadService } from '@/lib/services/slowLoadService';

interface LoadingMonitorOptions {
  /** Name of the operation for analytics */
  operationName: string;
  /** Threshold in ms after which to show warning (default: 3000) */
  warningThresholdMs?: number;
  /** Threshold in ms after which to show error (default: 8000) */
  errorThresholdMs?: number;
  /** Whether to show toast notifications (default: false) */
  showToast?: boolean;
  /** Screen ID for analytics context */
  screenId?: string;
}

/**
 * Monitors loading state and shows user feedback for slow operations
 * Also logs performance metrics to Sentry and Amplitude
 */
export function useLoadingMonitor(
  isLoading: boolean,
  options: LoadingMonitorOptions
) {
  const {
    operationName,
    warningThresholdMs = 3000,
    errorThresholdMs = 8000,
    showToast = false,
    screenId,
  } = options;

  const startTimeRef = useRef<number | null>(null);
  const warningShownRef = useRef(false);
  const errorShownRef = useRef(false);

  useEffect(() => {
    if (isLoading) {
      // Start tracking
      startTimeRef.current = performance.now();
      warningShownRef.current = false;
      errorShownRef.current = false;

      // Warning timer
      const warningTimer = setTimeout(() => {
        const currentDuration = startTimeRef.current 
          ? performance.now() - startTimeRef.current 
          : warningThresholdMs;
        
        if (showToast) {
          toast.warning('Загрузка занимает больше времени...', {
            duration: 3000,
            id: `loading-warning-${operationName}`,
          });
        }
        warningShownRef.current = true;
        
        // Track via slowLoadService → Sentry + Amplitude + app_logs
        slowLoadService.trackWarning({
          operationName,
          durationMs: currentDuration,
          thresholdMs: warningThresholdMs,
          screenId,
          route: window.location.pathname,
        });
      }, warningThresholdMs);

      // Error timer
      const errorTimer = setTimeout(() => {
        const currentDuration = startTimeRef.current 
          ? performance.now() - startTimeRef.current 
          : errorThresholdMs;
        
        if (showToast) {
          toast.error('Проблемы с загрузкой. Проверьте соединение.', {
            duration: 5000,
            id: `loading-error-${operationName}`,
            action: {
              label: 'Повторить',
              onClick: () => window.location.reload(),
            },
          });
        }
        errorShownRef.current = true;
        
        // Track via slowLoadService → Sentry + Amplitude + app_logs
        slowLoadService.trackError({
          operationName,
          durationMs: currentDuration,
          thresholdMs: errorThresholdMs,
          screenId,
          route: window.location.pathname,
        });
      }, errorThresholdMs);

      return () => {
        clearTimeout(warningTimer);
        clearTimeout(errorTimer);
      };
    } else if (startTimeRef.current !== null) {
      // Loading completed - record metrics
      const duration = performance.now() - startTimeRef.current;
      
      slowLoadService.trackComplete(
        operationName,
        duration,
        warningShownRef.current,
        errorShownRef.current,
        screenId
      );
      
      startTimeRef.current = null;
    }
  }, [isLoading, operationName, warningThresholdMs, errorThresholdMs, showToast, screenId]);
}

export default useLoadingMonitor;
